import { Content } from "@tiptap/core";
import { partialParse } from "openai-partial-json-parser";
import {
  AskAiChatMessageFragmentFragment,
  ChatbotRoleEnum,
  GetAskAiChatSessionHistoryQueryQuery,
  GetAskAiChatSessionQueryQuery,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import { assertEdgesNonNullWithStringId } from "@helpers/helpers";

export const askAIPlaceholderDebounceTime = 100;

// A "Test in Topicflow AI" / shared link pre-fills the Ask AI input via the `p`
// URL param. The param is free text that may LEAD with a skill reference:
//
//   /slug                     → a skill chip (slug precedence picks the skill)
//   /slug:skillId             → a chip bound to that exact skill
//   /slug:skillId free text   → that chip, then the rest as plain text
//
// skillId can itself contain a colon (platform built-ins are `platform:<slug>`),
// so we read it greedily up to the first space; slugs are kebab-case, so the
// first `:` reliably ends the slug. Anything that doesn't start with a `/slug`
// is returned untouched as plain text.
const SKILL_PROMPT_RE = /^\/([a-z0-9-]+)(?::(\S+))?(?:\s+([\s\S]*))?$/i;

export const buildInitialEditorContent = (prompt: string): Content => {
  const match = SKILL_PROMPT_RE.exec(prompt);
  if (!match) return prompt;
  const [, slug, skillId, rest] = match;
  // The chip's tag is cosmetic; only platform ids tell us the scope for sure
  // (built-ins live in the org). Everything else shows no tag.
  const scope = skillId?.startsWith("platform:") ? "PLATFORM" : "PERSONAL";
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "skillMention",
            attrs: { slug, skillId: skillId ?? null, scope },
          },
          // Tiptap rejects empty text nodes, so always emit at least a space —
          // it also separates the chip from any trailing text in getText().
          { type: "text", text: rest ? ` ${rest}` : " " },
        ],
      },
    ],
  };
};

export const createChatMessageNode = (
  partialChatMessage: Partial<AskAiChatMessageFragmentFragment>,
): AskAiChatMessageFragmentFragment => ({
  __typename: "ChatMessageNode",
  id: uuidv4(),
  role: ChatbotRoleEnum.User,
  content: "",
  imageUrls: [],
  documents: [],

  error: false,
  autogenerated: false,
  isStreaming: false,
  sender: null,

  createdArtifact: null,
  toolCalls: [],

  ...partialChatMessage,
});

export const parsePartialJson = (jsonStr: string) => {
  return partialParse(jsonStr);
};

const TAG_ATTR_RE = /([a-zA-Z_][\w-]*)="([^"]*)"/g;

const parseTagAttrs = (attrs: string): Record<string, string> => {
  const parsedAttrs: Record<string, string> = {};
  for (const match of attrs.matchAll(TAG_ATTR_RE)) {
    parsedAttrs[match[1]] = match[2];
  }
  return parsedAttrs;
};

const base64UrlEncodeJson = (data: Record<string, unknown>): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(data));
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    "",
  );
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const base64UrlDecodeJson = (value?: string): Record<string, any> => {
  if (!value) return {};
  try {
    const base64 = value
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(value.length / 4) * 4, "=");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return {};
  }
};

export const createSuggestedAssessmentResponseTag = ({
  suggestedResponse,
  citations = [],
  suggestionId,
}: {
  suggestedResponse: string;
  citations?: any[];
  suggestionId?: string | null;
}): string =>
  `<tf-suggested-assessment-response data-props="${base64UrlEncodeJson({
    citations,
    suggestion_id: suggestionId || uuidv4(),
  })}">${suggestedResponse}</tf-suggested-assessment-response>`;

// --- extractTextFromContent ---

const extractTitlesFromLegacyItem = (item: any): string => {
  if (!item || typeof item !== "object") return "";
  if (item.type === "suggested_assessment_responses") {
    const firstResponse = item.items?.[0]?.suggested_response;
    return typeof firstResponse === "string" ? firstResponse : "";
  }
  const title = item.title || item.name;
  if (typeof title === "string") return `- ${title}\n`;
  const items = item.items;
  if (Array.isArray(items)) {
    return items
      .map((sub: any) => {
        const subTitle = sub?.title || sub?.name;
        return typeof subTitle === "string" ? `- ${subTitle}\n` : "";
      })
      .join("");
  }
  const overview = item.overview;
  if (typeof overview === "string") return overview + "\n";
  return "";
};

export const extractTextFromContent = (content: string): string => {
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return content;
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed?.output_items)) {
      return parsed.output_items
        .map((item: any) => {
          if (item?.type === "plain_text" && typeof item?.text === "string")
            return item.text;
          return extractTitlesFromLegacyItem(item);
        })
        .join("");
    }
    return "";
  } catch {
    return content;
  }
};

// --- splitAssessmentResponseTags ---

export type AssessmentResponseData = {
  suggestedResponse: string;
  citations: {
    title: string;
    url: string;
    sourceId: string;
    sourceProvider?: string | null;
    sourceType?: string | null;
  }[];
  suggestionId: string;
};

const SUGGESTED_ASSESSMENT_RESPONSE_TAG_RE =
  /<tf-suggested-assessment-response([^>]*)>([\s\S]*?)<\/tf-suggested-assessment-response>|<tf_suggested_assessment_response>([\s\S]*?)<\/tf_suggested_assessment_response>/g;
const OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS = [
  "<tf-suggested-assessment-response",
  "<tf_suggested_assessment_response>",
];
const CLOSE_SUGGESTED_ASSESSMENT_RESPONSE_TAGS = [
  "</tf-suggested-assessment-response>",
  "</tf_suggested_assessment_response>",
];

export type MessageSource = { title: string; url: string };

const ALLOWED_SOURCE_URL_PROTOCOLS = new Set([
  "http:",
  "https:",
  "mailto:",
  "tel:",
]);

export const isSafeMessageSourceUrl = (url: string): boolean => {
  try {
    return ALLOWED_SOURCE_URL_PROTOCOLS.has(
      new URL(url, window.location.origin).protocol,
    );
  } catch {
    return false;
  }
};

/**
 * Best-effort provider for a source URL, so the sources list can show the
 * integration's logo (Topicflow swirl for internal links, GitHub/Linear/... for
 * external ones). Returns null for unknown external hosts so the caller can fall
 * back to a generic link icon instead of mislabeling them as Topicflow.
 */
export const providerFromUrl = (url: string): string | null => {
  if (!url) {
    return null;
  }
  // Relative Topicflow paths (e.g. /meeting/1/2) have no host.
  if (url.startsWith("/")) {
    return "topicflow";
  }
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host.includes("topicflow")) return "topicflow";
  if (host.includes("github")) return "github";
  if (host.includes("linear.app")) return "linear";
  if (host.includes("atlassian") || host.includes("jira")) return "jira";
  if (host.includes("hubspot")) return "hubspot";
  if (host.includes("youtrack")) return "youtrack";
  if (host.includes("clickup")) return "clickup";
  if (host.includes("salesforce") || host.includes("force.com"))
    return "salesforce";
  return null;
};

const TF_SOURCES_TAG_RE = /<tf-sources>([\s\S]*?)<\/tf-sources>/;
const SOURCE_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const TF_SOURCES_OPEN_TAG = "<tf-sources>";

/**
 * Pull the model's `<tf-sources>` block out of a message.
 *
 * Returns the answer text with the block removed, plus the parsed sources
 * (title + url per bullet). While streaming, an unclosed `<tf-sources>` is
 * hidden (its partial text is stripped and no sources are returned yet) so the
 * raw tag never flashes on screen.
 */
export const extractSourcesBlock = (
  text: string,
): { text: string; sources: MessageSource[] } => {
  const closed = text.match(TF_SOURCES_TAG_RE);
  if (closed) {
    const sources: MessageSource[] = [];
    for (const link of closed[1].matchAll(SOURCE_LINK_RE)) {
      sources.push({ title: link[1].trim(), url: link[2].trim() });
    }
    return { text: text.replace(closed[0], "").trimEnd(), sources };
  }
  const openIndex = text.indexOf(TF_SOURCES_OPEN_TAG);
  if (openIndex !== -1) {
    return { text: text.slice(0, openIndex).trimEnd(), sources: [] };
  }
  return { text, sources: [] };
};

const normalizeCitation = (citation: any) => {
  if (typeof citation === "string") {
    return { title: citation, url: "", sourceId: citation };
  }
  return {
    title:
      citation?.title || citation?.source_title || citation?.sourceId || "",
    url: citation?.url || citation?.source_url || "",
    sourceId: citation?.sourceId || citation?.source_id || "",
    sourceProvider:
      citation?.sourceProvider || citation?.source_provider || null,
    sourceType: citation?.sourceType || citation?.source_type || null,
  };
};

export const splitAssessmentResponseTags = (
  text: string,
  { isStreaming = false }: { isStreaming?: boolean } = {},
): Array<string | AssessmentResponseData> => {
  const segments: Array<string | AssessmentResponseData> = [];
  let cursor = 0;

  for (const match of text.matchAll(SUGGESTED_ASSESSMENT_RESPONSE_TAG_RE)) {
    let before = text.slice(cursor, match.index);
    if (isStreaming && before) {
      const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map(
        (tag) => before.indexOf(tag),
      ).filter((i) => i >= 0);
      const openTagIndex = openTagIndexes.length
        ? Math.min(...openTagIndexes)
        : -1;
      if (openTagIndex >= 0) before = before.slice(0, openTagIndex);
    }
    if (before) segments.push(before);

    try {
      if (match[2] !== undefined) {
        const attrs = parseTagAttrs(match[1] || "");
        const props = base64UrlDecodeJson(attrs["data-props"]);
        const suggestedResponse = match[2].trim();
        if (suggestedResponse) {
          segments.push({
            suggestedResponse,
            citations: Array.isArray(props.citations)
              ? props.citations.map(normalizeCitation)
              : [],
            suggestionId: props.suggestion_id || props.suggestionId || uuidv4(),
          });
        }
      } else {
        const data = JSON.parse(match[3]);
        const suggestedResponse =
          data?.suggested_response || data?.suggestedResponse || "";
        if (suggestedResponse) {
          segments.push({
            suggestedResponse,
            citations: Array.isArray(data?.citations)
              ? data.citations.map(normalizeCitation)
              : [],
            suggestionId: data?.suggestion_id || data?.suggestionId || uuidv4(),
          });
        }
      }
    } catch {
      segments.push(match[0]);
    }
    cursor = (match.index || 0) + match[0].length;
  }

  let remaining = text.slice(cursor);
  if (isStreaming) {
    const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map((tag) =>
      remaining.indexOf(tag),
    ).filter((i) => i >= 0);
    const openTagIndex = openTagIndexes.length
      ? Math.min(...openTagIndexes)
      : -1;
    if (openTagIndex >= 0) remaining = remaining.slice(0, openTagIndex);
  }
  if (remaining) segments.push(remaining);

  return segments;
};

// --- getMessageCopyText ---

// Produces clean, paste-ready text for a chat message: flattens JSON
// output_items and strips the tf-suggested-assessment-response wrapper tags,
// keeping only the inner suggested-response text (what the user actually sees).
export const getMessageCopyText = (content: string): string => {
  const { text } = extractSourcesBlock(extractTextFromContent(content));
  return splitAssessmentResponseTags(text)
    .map((segment) =>
      typeof segment === "string" ? segment : segment.suggestedResponse,
    )
    .join("")
    .trim();
};

export const getStreamingAssessmentResponseText = (
  text: string,
): string | null => {
  const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map((tag) =>
    text.lastIndexOf(tag),
  );
  const openTagIndex = Math.max(...openTagIndexes);
  if (openTagIndex < 0) return null;

  const hasClosingTagAfterOpen = CLOSE_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.some(
    (tag) => text.indexOf(tag, openTagIndex) >= 0,
  );
  if (hasClosingTagAfterOpen) return null;

  const contentStart = text.indexOf(">", openTagIndex);
  if (contentStart < 0) return "";
  return text.slice(contentStart + 1).trim();
};

// --- getChatSessionTitle ---

export const getChatSessionTitle = (
  chatSession:
    | GetAskAiChatSessionQueryQuery["chatSession"]
    | NonNullable<
        NonNullable<
          GetAskAiChatSessionHistoryQueryQuery["chatSessions"]
        >["edges"][number]
      >["node"],
) => {
  if (chatSession?.title) {
    return chatSession.title;
  }
  if (chatSession?.assessmentQuestion?.title) {
    return `Context: ${chatSession.assessmentQuestion.title}`;
  }
  const messages = chatSession?.messages
    ? assertEdgesNonNullWithStringId(chatSession.messages)
    : [];
  const firstMessage = messages.find(
    (message) => message.role === ChatbotRoleEnum.User,
  );
  return (
    firstMessage?.content ||
    firstMessage?.documents?.[0]?.filename ||
    (firstMessage?.imageUrls.length ? "Image message" : undefined)
  );
};
